import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getRouters } from '@/api/menu'
import router from '@/router'

const viewModules = require.context('@/views', true, /\.vue$/)
const viewModuleKeys = new Set(viewModules.keys())

export const useRouterStore = defineStore('router', () => {
  const dynamicRoutes = ref([])
  const isRoutesLoaded = ref(false)

  async function loadDynamicRoutes() {
    if (isRoutesLoaded.value) {
      return true
    }

    try {
      const data = await getRouters()
      if (data && data.length > 0) {
        const { routes, flatRoutes } = buildRouteTree(data)

        routes.forEach(route => {
          router.addRoute(route)
        })

        flatRoutes.forEach(route => {
          router.addRoute(route)
        })

        dynamicRoutes.value = routes
        isRoutesLoaded.value = true
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to load dynamic routes', error)
      return false
    }
  }

  function generateRouteName(path) {
    if (!path) return 'Route'
    return path
      .split('/')
      .filter(Boolean)
      .map(seg => seg.charAt(0).toUpperCase() + seg.slice(1))
      .join('')
  }

  function normalizePath(path, leadingSlash = true) {
    const trimmed = (path || '').replace(/^\/+|\/+$/g, '')
    if (!trimmed) return leadingSlash ? '/' : ''
    return leadingSlash ? `/${trimmed}` : trimmed
  }

  function resolveRedirect(fullPath, children, explicitRedirect) {
    if (explicitRedirect) return explicitRedirect
    const firstChild = (children || []).find(child => !child.meta?.hidden)
    if (!firstChild) return undefined
    const childPath = normalizePath(firstChild.path, false)
    return childPath ? `${fullPath}/${childPath}` : undefined
  }

  function buildRouteTree(data) {
    const routes = []
    const flatRoutes = []

    data
      .filter(item => !item.meta?.hidden)
      .forEach(item => {
        const itemPath = item.path || ''
        const fullPath = normalizePath(itemPath, true)

        const hasChildren = item.children && item.children.length > 0
        const isLayout = item.type === 0 || item.component?.includes('Layout')

        const route = {
          path: fullPath,
          name: generateRouteName(itemPath),
          component: isLayout || (hasChildren && !item.component)
            ? () => import('@/components/Layout/MainLayout.vue')
            : loadView(item.component),
          redirect: hasChildren ? resolveRedirect(fullPath, item.children, item.redirect) : item.redirect,
          meta: {
            title: item.meta?.title || item.name || '',
            icon: item.meta?.icon || '',
            hidden: item.meta?.hidden || false,
            affix: item.meta?.affix || false,
            noCache: item.meta?.noCache || false,
            breadcrumb: item.meta?.breadcrumb !== false,
            activeMenu: item.meta?.activeMenu || ''
          }
        }

        if (!hasChildren && !isLayout) {
          const namePath = `${normalizePath(itemPath, false)}/Index`
          route.component = () => import('@/components/Layout/MainLayout.vue')
          route.children = [
            {
              path: '',
              name: generateRouteName(namePath),
              component: loadView(item.component),
              meta: route.meta
            }
          ]
        }

        if (hasChildren) {
          const validChildren = item.children.filter(child => !child.meta?.hidden)
          if (validChildren.length > 0) {
            if (isLayout) {
              route.children = validChildren.map(child => buildChildRoute(child, itemPath, fullPath, true))
            } else {
              validChildren.forEach(child => {
                const childRoute = buildChildRoute(child, itemPath, fullPath, false)
                if (childRoute) {
                  flatRoutes.push(childRoute)
                }
              })
            }
          }
        }

        routes.push(route)
      })

    return { routes, flatRoutes }
  }

  function buildChildRoute(child, parentPath = '', parentFullPath = '', isNested = true) {
    const childPath = child.path || ''
    const childPathNoSlash = normalizePath(childPath, false)
    // isNested=true 时 path 已经是相对于 parent 的完整路径，无需再加 parentFullPath
    const fullChildPath = isNested
      ? childPathNoSlash
      : (parentFullPath ? `${parentFullPath}/${childPathNoSlash}` : normalizePath(childPath, true))
    const namePath = parentPath
      ? `${normalizePath(parentPath, false)}/${childPathNoSlash}`
      : childPathNoSlash

    return {
      path: isNested ? childPathNoSlash : fullChildPath,
      name: generateRouteName(namePath),
      component: loadView(child.component),
      meta: {
        title: child.meta?.title || child.name || '',
        icon: child.meta?.icon || '',
        hidden: child.meta?.hidden || false,
        affix: child.meta?.affix || false,
        noCache: child.meta?.noCache || false,
        breadcrumb: child.meta?.breadcrumb !== false,
        activeMenu: child.meta?.activeMenu || ''
      }
    }
  }

  function loadView(component) {
    if (!component) {
      return () => import('@/views/PageNotFound.vue')
    }

    if (component === 'Layout' || component.includes('Layout')) {
      return () => import('@/components/Layout/MainLayout.vue')
    }

    let componentPath = component
    if (componentPath.startsWith('@/')) {
      componentPath = componentPath.slice(2)
    } else if (componentPath.startsWith('/src/')) {
      componentPath = componentPath.replace(/^\/src\//, '')
    }

    componentPath = componentPath.replace(/^\/+/, '')
    componentPath = componentPath.replace(/^views\//, '')
    componentPath = componentPath.replace(/\.vue$/, '')

    const modulePath = `./${componentPath}.vue`
    if (!viewModuleKeys.has(modulePath)) {
      return () => import('@/views/PageNotFound.vue')
    }

    return () => Promise.resolve(viewModules(modulePath)).then(mod => mod.default || mod)
  }

  function resetRoutes() {
    dynamicRoutes.value = []
    isRoutesLoaded.value = false
  }

  return {
    dynamicRoutes,
    isRoutesLoaded,
    loadDynamicRoutes,
    resetRoutes
  }
})
